import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, withAsOverride } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { brandingKeys } from "@/lib/query";
import { ASSET_SLUGS, type AssetSlug, type Branding, brandingSchema } from "@/schemas/branding";

const ASSET_LABEL: Record<AssetSlug, string> = {
  [ASSET_SLUGS.homepageLogo]: "Logo",
  [ASSET_SLUGS.userGuide]: "User guide",
};

/** Metadata for the homepage logo + question-mark user guide. The file bytes
 *  are NOT in this payload — render them from `/api/branding/:slug/content`. */
export function useBranding() {
  return useQuery<Branding>({
    queryKey: brandingKeys.all,
    queryFn: () => apiGet("/api/branding", brandingSchema),
  });
}

/** Public content URL for an asset. `download` flips the response to an
 *  attachment (Save dialog) rather than inline display. */
export function assetContentUrl(slug: AssetSlug, opts?: { download?: boolean }): string {
  const base = `/api/branding/${slug}/content`;
  return withAsOverride(opts?.download ? `${base}?download=1` : base);
}

/** Upload (replace) an asset via multipart form. apiSend is JSON-only, so this
 *  posts FormData directly. */
export function useUploadAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug, file }: { slug: AssetSlug; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(withAsOverride(`/api/branding/${slug}/content`), {
        method: "PUT",
        body: form,
      });
      if (!res.ok) {
        let msg = `Upload failed: ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string };
          msg = body.error ?? msg;
        } catch {
          // non-JSON error body — keep the status message
        }
        throw new Error(msg);
      }
    },
    onSuccess: (_data, { slug }) => {
      toast.success(`${ASSET_LABEL[slug]} updated`);
      queryClient.invalidateQueries({ queryKey: brandingKeys.all });
    },
    onError: (error, { slug }) => {
      logger.error("branding_upload_failed", { error, slug });
      toast.error(error instanceof Error ? error.message : `Failed to update ${ASSET_LABEL[slug]}`);
    },
  });
}

/** Clear an asset, reverting to the built-in default. */
export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug }: { slug: AssetSlug }) => {
      const res = await fetch(withAsOverride(`/api/branding/${slug}/content`), {
        method: "DELETE",
      });
      if (!res.ok) {
        let msg = `Remove failed: ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string };
          msg = body.error ?? msg;
        } catch {
          // non-JSON error body — keep the status message
        }
        throw new Error(msg);
      }
    },
    onSuccess: (_data, { slug }) => {
      toast.success(`${ASSET_LABEL[slug]} removed`);
      queryClient.invalidateQueries({ queryKey: brandingKeys.all });
    },
    onError: (error, { slug }) => {
      logger.error("branding_delete_failed", { error, slug });
      toast.error(error instanceof Error ? error.message : `Failed to remove ${ASSET_LABEL[slug]}`);
    },
  });
}
